import PaginatedList from "@theme/paginated-list";

/**
 * A custom element that renders a paginated blog posts list
 */
export default class BlogPostsList extends PaginatedList {
  /** @type {string} */
  #activeFilterTag = "";

  /** @type {MutationObserver | null} */
  #gridObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this.#handleFilterClick);
    this.#observeGrid();
    this.#applyFilter();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.#handleFilterClick);
    this.#gridObserver?.disconnect();
    this.#gridObserver = null;
  }

  #handleFilterClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const filterButton = target.closest("[data-filter-tag]");
    if (
      !(filterButton instanceof HTMLButtonElement) ||
      !this.contains(filterButton)
    )
      return;

    const clickedTag = (filterButton.dataset.filterTag || "")
      .trim()
      .toLowerCase();
    if (!clickedTag) return;

    this.#activeFilterTag =
      this.#activeFilterTag === clickedTag ? "" : clickedTag;
    this.#syncButtonsState();
    this.#applyFilter();
  };

  #syncButtonsState() {
    const filterButtons = this.querySelectorAll("[data-filter-tag]");

    for (const button of filterButtons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const buttonTag = (button.dataset.filterTag || "").trim().toLowerCase();
      const isActive = buttonTag !== "" && buttonTag === this.#activeFilterTag;
      button.setAttribute("aria-pressed", String(isActive));
    }
  }

  #applyFilter() {
    const blogItems = this.querySelectorAll(".blog-post-item[data-blog-tags]");

    for (const item of blogItems) {
      if (!(item instanceof HTMLElement)) continue;

      const tags = (item.dataset.blogTags || "")
        .split("|")
        .map((tag) => tag.trim().toLowerCase())
        .filter(Boolean);

      const shouldShow =
        this.#activeFilterTag === "" || tags.includes(this.#activeFilterTag);

      item.hidden = !shouldShow;
      item.setAttribute("aria-hidden", String(!shouldShow));
    }
  }

  #observeGrid() {
    const grid = this.querySelector('[ref="grid"]');
    if (!(grid instanceof HTMLElement)) return;

    this.#gridObserver?.disconnect();
    this.#gridObserver = new MutationObserver(() => this.#applyFilter());
    this.#gridObserver.observe(grid, { childList: true });
  }
}

if (!customElements.get("blog-posts-list")) {
  customElements.define("blog-posts-list", BlogPostsList);
}
